import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import SoftAlert from "../../../components/Alert";
import axiosApiClient from "../../../api/axios";
import { verifyOtp } from "../../../api/auth/authApiClient";
import { getResetOtpResendSeconds } from "../../../utils/config";

function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialEmail = location.state?.email || "";
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);
  const [resendTimer, setResendTimer] = useState(0); // seconds remaining
  const [otpRequested, setOtpRequested] = useState(false);
  const isOtpComplete = otp.every((digit) => digit !== "");
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isEmailValid = emailRegex.test(email.trim());
  const resendIntervalSeconds = getResetOtpResendSeconds();

  const handleSendOtp = async (targetEmail) => {
    const trimmedEmail = targetEmail.trim();
    if (!trimmedEmail) {
      setError("Please enter your email");
      setSuccess(null);
      setShowAlert(true);
      return;
    }

    setLoading(true);
    try {
      setError(null);
      setSuccess(null);
      setShowAlert(false);

      const response = await axiosApiClient.post("/api/auth/reset-otp", {
        email: trimmedEmail,
      });

      const message =
        response?.data?.message ||
        "We sent the OTP code to your email. Please enter the OTP.";
      setSuccess(message);
      setShowAlert(true);
      setOtpRequested(true);
    } catch (err) {
      const data = err?.response?.data;
      const backendMessage =
        (data && typeof data === "string" && data) ||
        data?.message ||
        err?.message;
      setError(backendMessage || "Failed to send reset OTP email");
      setSuccess(null);
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  // Prefill email if it was passed from the sign-in page, but do not auto-send OTP
  useEffect(() => {
    if (initialEmail && !email) {
      setEmail(initialEmail);
    }
  }, [initialEmail, email]);

  // Countdown effect for resend timer
  useEffect(() => {
    if (resendTimer <= 0) return;

    const intervalId = setInterval(() => {
      setResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [resendTimer]);

  const handleResendClick = async () => {
    const targetEmail = (email || initialEmail || "").trim();
    if (!targetEmail) {
      setError("Email is missing for resend");
      setSuccess(null);
      setShowAlert(true);
      return;
    }

    await handleSendOtp(targetEmail);
    setResendTimer(resendIntervalSeconds);
  };

  const formatTimer = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(1, "0");
    const seconds = (totalSeconds % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isOtpComplete) {
      return;
    }

    const targetEmail = (email || initialEmail || "").trim();
    const otpCode = otp.join("");

    if (!targetEmail) {
      setError("Email is missing for OTP verification");
      setSuccess(null);
      setShowAlert(true);
      return;
    }

    setLoading(true);
    try {
      setError(null);
      setSuccess(null);
      setShowAlert(false);

      const response = await verifyOtp(targetEmail, otpCode);
      const resetToken = response?.data?.resetToken;
      if (!resetToken) {
        throw new Error("Failed to obtain password reset token");
      }

      // Navigate to page where user can set a new password, passing the reset token
      navigate("/authentication/set-new-password", {
        state: { email: targetEmail, resetToken },
      });
    } catch (err) {
      setError(err?.message || "Failed to verify OTP");
      setSuccess(null);
      setShowAlert(true);
    } finally {
      setLoading(false);
      // Clear OTP boxes after any verify attempt (success or failure)
      setOtp(["", "", "", "", "", ""]);
      if (otpRefs.current[0]) {
        otpRefs.current[0].focus();
      }
    }
  };

  const handleOtpChange = (index, e) => {
    const value = e.target.value.replace(/\D/g, "").slice(-1); // keep only last digit

    setOtp((prev) =>
      prev.map((digit, i) => (i === index ? value : digit))
    );

    // Auto-move to next box when a digit is entered
    if (value && index < otpRefs.current.length - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      // If current box is empty and Backspace is pressed, move focus back
      otpRefs.current[index - 1]?.focus();
    }
  };

  return (
    <CoverLayout
      title="Reset Password"
      description="Enter your email to receive an OTP code, then enter the OTP verify."
    >
      {error && (
        <SoftAlert
          color="error"
          dismissible={false}
          timeout={10}
          mount={showAlert}
          onClose={() => {
            setError(null);
            setShowAlert(false);
          }}
        >
          {error}
        </SoftAlert>
      )}
      {success && !error && (
        <SoftAlert
          color="success"
          dismissible={false}
          timeout={10}
          mount={showAlert}
          onClose={() => {
            setSuccess(null);
            setShowAlert(false);
          }}
        >
          {success}
        </SoftAlert>
      )}
      <SoftBox component="form" role="form" onSubmit={handleSubmit}>
        {!otpRequested && (
          <>
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                >
                  Email
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </SoftBox>
            <SoftBox mt={1} mb={3}>
              <SoftButton
                type="button"
                variant="gradient"
                color="primary"
                fullWidth
                disabled={!isEmailValid || loading}
                onClick={async () => {
                  await handleSendOtp(email);
                  setResendTimer(resendIntervalSeconds);
                }}
              >
                {loading ? "Sending OTP..." : "Send OTP"}
              </SoftButton>
            </SoftBox>
          </>
        )}
        {otpRequested && (
          <>
            <SoftBox mb={1}>
              <SoftTypography variant="button" color="text" fontWeight="regular">
                We sent an OTP to <strong>{email.trim()}</strong>. Please enter it below.
              </SoftTypography>
            </SoftBox>
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                >
                  OTP Code
                </SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="center" gap={1}>
                {otp.map((digit, index) => (
                  <SoftInput
                    key={index}
                    type="text"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    inputRef={(el) => {
                      otpRefs.current[index] = el;
                    }}
                    placeholder="-"
                    inputProps={{
                      maxLength: 1,
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      style: { textAlign: "center" },
                    }}
                    sx={{ width: "3rem" }}
                  />
                ))}
              </SoftBox>
            </SoftBox>
            <SoftBox mt={1} textAlign="center">
              {resendTimer > 0 ? (
                <SoftTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                >
                  Resend OTP in{" "}
                  <SoftTypography
                    component="span"
                    variant="button"
                    fontWeight="medium"
                  >
                    {formatTimer(resendTimer)}
                  </SoftTypography>
                </SoftTypography>
              ) : (
                <SoftTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                >
                  Didn&apos;t receive the code?{" "}
                  <SoftTypography
                    component="span"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    textGradient
                    sx={{ cursor: "pointer" }}
                    onClick={handleResendClick}
                  >
                    Resend OTP
                  </SoftTypography>
                </SoftTypography>
              )}
            </SoftBox>
            <SoftBox mt={4} mb={1}>
              <SoftButton
                type="submit"
                variant="gradient"
                color="primary"
                fullWidth
                disabled={loading || !isOtpComplete}
              >
                {loading ? "Verifying..." : "Verify"}
              </SoftButton>
            </SoftBox>
          </>
        )}
        <SoftBox mt={3} textAlign="center">
          <SoftTypography variant="button" color="text" fontWeight="regular">
            Remember your password?{" "}
            <SoftTypography
              component={Link}
              to="/authentication/sign-in"
              variant="button"
              color="info"
              fontWeight="medium"
              textGradient
            >
              Sign in
            </SoftTypography>
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </CoverLayout>
  );
}

export default ResetPassword;